<template>
  <DetailInfo class="offer-detail-container" :btnList="btnList">
    <CollapseContent v-if="offerLetterEventList.length" class="offer-letter-event-list" :title="$t(`${i18nPrefix}.title.offerRecord`)">
      <div
        v-for="(event, index) in offerLetterEventList"
        :key="index"
        class="offer-letter-event-item"
      >
        <div class="info">
          <div class="creator">{{ event.creator.name }}</div>
          <div class="candidate">
            <span class="label">{{ $t(`${i18nPrefix}.label.candidateName`) }}: </span>
            <span class="content">{{ event.candidate.name }}</span>
          </div>
          <div class="status">
            <span class="label">{{ $t(`${i18nPrefix}.label.status`) }}: </span>
            <span class="content">{{ event.statusChangeText }}</span>
          </div>
        </div>
        <div class="time">{{ event.created_time|formatDateTime('YYYY/MM/DD HH:mm') }}</div>
      </div>
    </CollapseContent>
    <div v-if="displayOfferLetterContent" class="offer-viewer-container">
      <div v-html="displayOfferLetterContent"></div>
    </div>
    <DialogWrapper
      close-on-cancel
      class="offer-letter-template-dialog"
      :show.sync="offerLetterTemplateDialog.show"
      :titleI18nKey="`${i18nPrefix}.title.offerLetterSelectTemplate`"
      @save="handleSelectTemplate"
    >
      <el-select v-model="selectedTemplateId">
        <el-option
          v-for="(template, index) in templateList"
          :key="index"
          :label="template.name"
          :value="template.id"
        ></el-option>
      </el-select>
    </DialogWrapper>
    <InviteOfferLetterDialog
      :show.sync="inviteOfferLetterDialog.show"
      :selectedTemplateId="inviteOfferLetterDialog.templateId"
      :templateList="templateList"
      :templateFieldList="templateFieldList"
      :candidate-name="candidateBaseInfo.other_name"
      :candidate-email="candidateBaseInfo.email"
      :candidate-first-name="candidateBaseInfo.first_name"
      :candidate-last-name="candidateBaseInfo.last_name"
      :company-id="candidateBaseInfo.company.id"
      :company-name="getI18nPropertyValue(candidateBaseInfo.company, 'cn_name', 'name')"
      :job-id="candidateBaseInfo.job.id"
      :job-name="getI18nPropertyValue(candidateBaseInfo.job, 'cn_title', 'title')"
      @save="handleSaveSendOffer"
      @cancel="handleCancelSendOffer"
      @preview="handlePreview"
    ></InviteOfferLetterDialog>
    <DialogWrapper
      appendToBody
      hideFooter
      fullscreen
      class="offer-letter-preview-dialog"
      :showClose="false"
      :show.sync="showOfferLetterPreviewDialog.show"
    >
      <PaletteButton
      class="palette-btn"
      :class="!showOfferLetterPreviewDialog.isPCPreview && 'mobile-palette-btn'"
      :isPCPreview="showOfferLetterPreviewDialog.isPCPreview"
      @changeDevice="handleChangeDevice"
      @closePreview="handleClosePreview"
      ></PaletteButton>
      <div v-if="showOfferLetterPreviewDialog.isPCPreview" class="pc-preview">
        <OfferLetterPc isPreview :previewOfferLetterInfo="showOfferLetterPreviewDialog.inviteOfferLetterInfo"></OfferLetterPc>
      </div>
      <div v-else class="mobile-preview">
        <OfferLetterMobile isPreview :previewOfferLetterInfo="showOfferLetterPreviewDialog.inviteOfferLetterInfo"></OfferLetterMobile>
      </div>
    </DialogWrapper>
  </DetailInfo>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapActions } from 'vuex'
import DetailInfo from './components/detailInfo.vue'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'
import InviteOfferLetterDialog from './dialog/inviteOfferLetterDialog.vue'
import CollapseContent from './components/collapseContent.vue'
import PaletteButton from '@/views/adminNew/job/editor/paletteButton.vue'
import OfferLetterPc from '@/views/personalNew/offerLetter/pc/index.vue'
import OfferLetterMobile from '@/views/personalNew/offerLetter/mobile/index.vue'
import { AsyncTask } from '@/utils/common'
import { getI18nPropertyValue, getConstantI18nValue, addI18nProperty } from '@/i18n'
import { OFFER_LETTER_EVENT_STATUS, OFFER_LETTER_TEMPLATE_FIELD_TYPE } from '@/constant/company'
import mixin from '@/views/personalNew/offerLetter/mixin'

export default {
  mixins: [mixin],
  components: {
    DetailInfo,
    DialogWrapper,
    InviteOfferLetterDialog,
    CollapseContent,
    PaletteButton,
    OfferLetterPc,
    OfferLetterMobile
  },
  props: {
    candidateBaseInfo: {
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      OFFER_LETTER_TEMPLATE_FIELD_TYPE,
      getI18nPropertyValue,
      i18nPrefix: 'admin.application.candidate',
      templateList: [],
      selectedTemplateId: null,
      templateFieldList: [],
      offerLetterTemplateDialog: {
        isResend: false,
        show: false
      },
      inviteOfferLetterDialog: {
        show: false,
        templateId: null
      },
      sendNoticeLoading: false,
      inviteOfferLetterInfo: {
        offer_letter_content: null
      },
      offerLetterEventList: [],
      exportOfferLetterPdfStatus: {
        loading: false,
        task: null,
        finished: false
      },
      offerLetterContentFieldInfo: {},
      showOfferLetterPreviewDialog: {
        show: false,
        isPCPreview: true,
        inviteOfferLetterInfo: {
          offer_letter_content: null
        }
      }
    }
  },
  computed: {
    candidateId () {
      return this.candidateBaseInfo.id
    },
    jobId () {
      return this.candidateBaseInfo.job.id
    },
    btnList () {
      if (this.candidateBaseInfo.is_agency_uploaded_and_hid) {
        return []
      }
      const vm = this
      const list = [
        {
          hidden: this.inviteOfferLetterInfo.id,
          label: this.$t(`${this.i18nPrefix}.btn.sendOfferLetter`),
          onclick: this.handleOpenInviteOffer
        },
        {
          hidden: !this.inviteOfferLetterInfo.id,
          label: this.$t(`${this.i18nPrefix}.btn.reSendInviteOfferLetter`, { date: moment(this.inviteOfferLetterInfo.created_time).format('YYYY/MM/DD') }),
          onclick: this.handleResendInviteOffer
        },
        {
          hidden: !this.inviteOfferLetterInfo.id || this.inviteOfferLetterInfo.status === 1 || this.inviteOfferLetterInfo.status === 2,
          get label () {
            if (vm.inviteOfferLetterInfo?.last_remind_time) {
              return vm.$t(`${vm.i18nPrefix}.btn.resendOfferLetterNotice`, { date: moment(vm.inviteOfferLetterInfo.last_remind_time).format('YYYY/MM/DD') })
            } else {
              return vm.$t(`${vm.i18nPrefix}.btn.sendOfferLetterNotice`)
            }
          },
          get loading () { return vm.sendNoticeLoading },
          onclick: this.handleSendInviteOfferNotice
        },
        {
          hidden: !this.inviteOfferLetterInfo.id,
          get loading () {
            const exportOfferLetterPdfStatus = vm.exportOfferLetterPdfStatus
            return exportOfferLetterPdfStatus?.task && !exportOfferLetterPdfStatus.finished
          },
          label: this.$t(`${this.i18nPrefix}.btn.exportOfferPdf`),
          onclick: this.handleExportPDF
        }
      ]
      _.forEach(list, (item) => {
        Object.defineProperty(item, 'disabled', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.isDisabled
          }
        })
        Object.defineProperty(item, 'tooltip', {
          enumerable: true,
          configurable: true,
          get () {
            return vm.isDisabled && vm.$t(`${vm.i18nPrefix}.message.disabledTip`)
          }
        })
      })
      return list
    },
    templateContentFields () {
      const content = this.inviteOfferLetterInfo.offer_letter_content
      if (!content) {
        return []
      }
      const allKeys = this.getTemplateAllPlaceholder(content)
      const templateFields = _.chain(this.allTemplateFiledList).filter(item => _.includes(allKeys, item.key)).map(item => {
        item.order = _.findIndex(allKeys, key => key === item.key)
        return item
      }).orderBy('order').value()
      return templateFields
    },
    displayOfferLetterContent () {
      if (this.inviteOfferLetterInfo.offer_letter_content) {
        if (this.inviteOfferLetterInfo?.status === 1) {
          return this.inviteOfferLetterInfo.offer_letter_content
        } else {
          return this.formatContent(this.inviteOfferLetterInfo.offer_letter_content, this.offerLetterContentFieldInfo)
        }
      }
      return ''
    }
  },
  created () {
    this.initPage()
  },
  mounted () {
    this.resetContentFieldInfo()
  },
  methods: {
    ...mapActions('offerLetter', ['getOfferLetterTemplateList', 'getOfferLetterTemplateFieldList']),
    ...mapActions('application', [
      'getApplicationCandidateOfferLetterInfo',
      'sendInviteCandidateOfferLetter',
      'resendInviteCandidateOfferLetter',
      'sendOfferLetterNotice',
      'createExportCandidateOfferLetterInfoTask',
      'getExportCandidateOfferLetterTaskStatus'
    ]),
    async initPage () {
      await this.loadTemplateFiledList()
      Promise.all([this.loadCandidateOfferLetterInfo(), this.loadTemplateList()])
    },
    async loadCandidateOfferLetterInfo () {
      const params = {
        job_id: this.jobId,
        candidate_id: this.candidateId
      }
      const resp = await this.getApplicationCandidateOfferLetterInfo(params)
      if (resp) {
        this.inviteOfferLetterInfo = resp
        this.offerLetterEventList = _.map(resp.offer_letter_event_list, event => {
          Object.defineProperty(event, 'statusChangeText', {
            configurable: true,
            enumerable: true,
            get () {
              const i18nPrefix = 'admin.company.offerLetterInviteRecordManagement'
              const newStatusText = getConstantI18nValue(OFFER_LETTER_EVENT_STATUS, event.new_status, `${i18nPrefix}.OFFER_EVENT_STATUS`)
              const oldStatusText = getConstantI18nValue(OFFER_LETTER_EVENT_STATUS, event.old_status, `${i18nPrefix}.OFFER_EVENT_STATUS`)
              if (newStatusText && oldStatusText) {
                return `${oldStatusText} -> ${newStatusText}`
              } else if (oldStatusText) {
                return oldStatusText
              } else {
                return newStatusText
              }
            }
          })
          return event
        })
        this.$emit('updateOfferLetterStatusColor', this.inviteOfferLetterInfo.status)
        this.resetContentFieldInfo()
      }
    },
    async loadTemplateList () {
      const resp = await this.getOfferLetterTemplateList({ disabled_pagination: 1, is_enabled: 1 })
      if (resp) {
        this.templateList = resp.result
      }
    },
    async loadTemplateFiledList () {
      const resp = await this.getOfferLetterTemplateFieldList()
      if (resp) {
        _.forEach(resp, item => {
          _.forEach(item.option_list, option => {
            addI18nProperty(option, 'i18nName', 'name_cn', 'name_en')
          })
        })
        this.allTemplateFiledList = resp
        this.templateFieldList = _.filter(this.allTemplateFiledList, { type: 'HR' })
      }
    },
    resetContentFieldInfo () {
      _.forEach(this.templateContentFields, field => {
        if (!_.has(this.offerLetterContentFieldInfo, field.key)) {
          this.offerLetterContentFieldInfo[field.key] = null
        }
      })
    },
    handleOpenInviteOffer () {
      this.offerLetterTemplateDialog.show = true
    },
    handleSelectTemplate () {
      if (!this.selectedTemplateId) {
        return
      }
      this.offerLetterTemplateDialog.show = false
      this.inviteOfferLetterDialog.templateId = this.selectedTemplateId
      this.inviteOfferLetterDialog.show = true
    },
    async handleSendInviteOfferNotice () {
      this.sendNoticeLoading = true
      try {
        const params = {
          job_id: this.jobId,
          candidate_id: this.candidateId
        }
        const resp = await this.sendOfferLetterNotice(params)
        if (resp) {
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.sendOfferNoticeSuccess`)
          })
          this.loadCandidateOfferLetterInfo()
        }
      } finally {
        this.sendNoticeLoading = false
      }
    },
    handleResendInviteOffer () {
      this.offerLetterTemplateDialog.show = true
      this.offerLetterTemplateDialog.isResend = true
    },
    handleExportPDF () {
      const exportOfferLetterPdfStatus = this.exportOfferLetterPdfStatus
      if (exportOfferLetterPdfStatus.loading) {
        return
      }
      exportOfferLetterPdfStatus.loading = true
      exportOfferLetterPdfStatus.finished = false
      exportOfferLetterPdfStatus.task = new AsyncTask(() => this.createExportCandidateOfferLetterInfoTask({
        candidate_id: this.candidateId,
        job_id: this.jobId
      }), (taskId) => this.getExportCandidateOfferLetterTaskStatus({
        task_id: taskId
      }), (taskId) => {
        window.location.href = this.$DjangoBaseUrl(`/django_rest/company/job/application/export/offer-letter/?task_id=${taskId}&download=1`)
        exportOfferLetterPdfStatus.loading = false
        exportOfferLetterPdfStatus.finished = false
        exportOfferLetterPdfStatus.task = null
      })
    },
    async handleSaveSendOffer (offerLetterInfo, callback) {
      try {
        const params = {
          job_id: this.jobId,
          candidate_id: this.candidateId,
          template_id: this.selectedTemplateId,
          template_field_info: this.formatOfferLetterInfo(offerLetterInfo)
        }
        let resp
        if (this.offerLetterTemplateDialog.isResend) {
          resp = await this.resendInviteCandidateOfferLetter(params)
        } else {
          resp = await this.sendInviteCandidateOfferLetter(params)
        }
        if (resp) {
          this.loadCandidateOfferLetterInfo()
          this.inviteOfferLetterDialog.show = false
          this.$message({
            type: 'success',
            message: this.$t(`${this.i18nPrefix}.message.${this.offerLetterTemplateDialog.isResend ? 'reInviteOfferSuccess' : 'inviteOfferSuccess'}`)
          })
        }
      } finally {
        callback()
      }
    },
    handleCancelSendOffer () {
      this.inviteOfferLetterDialog.show = false
    },
    handlePreview (offerLetterInfo, selectedTemplateId) {
      const template = _.find(this.templateList, { id: selectedTemplateId })
      if (!template) {
        return
      }
      this.showOfferLetterPreviewDialog.inviteOfferLetterInfo.offer_letter_content = this.formatContent(template.content, offerLetterInfo)
      this.showOfferLetterPreviewDialog.isPCPreview = true
      this.showOfferLetterPreviewDialog.show = true
    },
    handleChangeDevice () {
      this.showOfferLetterPreviewDialog.isPCPreview = !this.showOfferLetterPreviewDialog.isPCPreview
    },
    handleClosePreview () {
      this.showOfferLetterPreviewDialog.show = false
    }
  }
}
</script>

<style lang="scss" scoped>
.offer-detail-container {
  .offer-letter-event-list {
    width: 822px;
    margin: 0 auto;

    .offer-letter-event-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: #606266;
      font-size: 14px;
      border-bottom: 1px solid #EBEEF5;
      padding: 15px 0;
      &:last-child {
        border-bottom: 0;
        padding-bottom: 0;
      }
      .info {
        .creator {
          margin-bottom: 4px;
          font-weight: bold;
          font-size: 16px;
          color: #606266;
        }
        .candidate {
          color: #606266;
        }
        .status {
          color: #606266;
        }
      }
      .time {
        flex-shrink: 0;
        margin-left: 20px;
      }
    }
  }
  .offer-viewer-container {
    width: 822px;
    margin: 0 auto;
    padding: 96px 120px;
    border: 1px solid #c0c4cc;
  }
}
.offer-letter-preview-dialog {
  .palette-btn {
    right: 100px;
  }
  .mobile-palette-btn {
    left: 50%;
    transform: translate(250px);
    top: 88px;
  }
  .mobile-preview {
    border: 1px solid #c0c4cc;
    width: 432px;
    min-height: 716px;
    margin: auto;
    background-color: #fff;
  }
  ::v-deep {
    .el-dialog {
      &::before {
        display: none;
      }
    }
  }
}
</style>
